import { Check } from "lucide-react";
import { cn } from "@headerhawk/ui";
import {
  TRIAL_DAYS,
  tierHasTrial,
  type TierOption,
  type BillingInterval,
} from "./tier-data";
import { getAnnualSavings } from "./tier-data";

interface TierCardProps {
  tier: TierOption;
  isSelected: boolean;
  onSelect: () => void;
  disabled?: boolean;
  billingInterval?: BillingInterval;
}

function formatNumber(num: number): string {
  if (num >= 1_000_000) {
    return `${num / 1_000_000}M`;
  }
  if (num >= 1_000) {
    return `${num / 1_000}k`;
  }
  return num.toString();
}

export function TierCard({
  tier,
  isSelected,
  onSelect,
  disabled = false,
  billingInterval = "monthly",
}: TierCardProps) {
  const displayPrice =
    billingInterval === "annual" ? tier.price.annual : tier.price.monthly;
  const isFree = tier.price.monthly === 0;
  const annualSavings = getAnnualSavings(tier);
  const showSavingsBadge =
    billingInterval === "annual" && !isFree && annualSavings > 0;

  return (
    <button
      type="button"
      role="radio"
      onClick={disabled ? undefined : onSelect}
      aria-checked={isSelected}
      aria-disabled={disabled}
      disabled={disabled}
      className={cn(
        "relative flex flex-col p-6 rounded-lg border-2 text-left transition-all duration-200",
        "focus:outline-hidden focus-visible:focus-ring",
        isSelected && "border-primary bg-primary/5 shadow-md",
        !isSelected && "border-border shadow-xs",
        tier.popular && !isSelected && "border-primary/30",
        disabled
          ? "cursor-not-allowed opacity-60"
          : "hover:border-primary/50 hover:shadow-lg hover:-translate-y-1 cursor-pointer",
      )}
    >
      {tier.popular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
          Most Popular
        </span>
      )}

      <div className="mb-4">
        <h3 className="text-lg font-semibold">{tier.name}</h3>
      </div>

      <div className="mb-6 min-h-18">
        <div className="flex items-baseline gap-1 whitespace-nowrap">
          <span className="text-3xl font-bold">
            {isFree ? "Free" : `$${displayPrice}`}
          </span>
          {!isFree && (
            <span className="text-muted-foreground text-sm">
              /{billingInterval === "annual" ? "yr" : "mo"}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-2 mt-1 min-h-5">
          {showSavingsBadge && (
            <span className="text-xs font-medium text-green-600 dark:text-green-400">
              Save ${annualSavings}/year
            </span>
          )}
          {tierHasTrial(tier.id) && !isFree && (
            <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
              {TRIAL_DAYS}-day free trial
            </span>
          )}
        </div>
      </div>

      <ul className="space-y-3 mb-4">
        <li className="text-sm text-muted-foreground">
          {tier.features.sites} {tier.features.sites === 1 ? "site" : "sites"}
        </li>
        <li className="text-sm text-muted-foreground">
          {formatNumber(tier.features.monthlyReports)} reports/month
        </li>
        <li className="text-sm text-muted-foreground">
          {tier.features.retention}-day retention
        </li>
        <li className="text-sm text-muted-foreground">
          {tier.features.users} {tier.features.users === 1 ? "user" : "users"}
        </li>
      </ul>

      <div className="border-t pt-4 space-y-2 flex-1">
        {tier.highlights.map((highlight) => (
          <div key={highlight} className="flex items-start gap-2 text-sm">
            <Check
              className="h-4 w-4 text-primary mt-0.5 shrink-0"
              aria-hidden="true"
            />
            <span>{highlight}</span>
          </div>
        ))}
      </div>
    </button>
  );
}
